import { useEffect, useState } from 'react';
import api from '../lib/api';
import { FileText, Plus, Edit2, Trash2, Star } from 'lucide-react';
import Modal from '../components/Modal';
import { Input } from '../components/Form';

interface ReceiptTemplate {
  id: string;
  name: string;
  content: string;
  isDefault: boolean;
  isActive: boolean;
}

const VARS = ['{{numero}}', '{{pagador}}', '{{valor}}', '{{competencia}}', '{{forma}}', '{{dataPagamento}}', '{{imovel}}', '{{contrato}}', '{{cidade}}', '{{data}}', '{{emitente}}'];

const STARTER = `RECIBO Nº {{numero}}

Recebemos de {{pagador}} a quantia de R$ {{valor}}, referente a locação do imóvel {{imovel}} (competência {{competencia}}), na forma de {{forma}}.

Contrato: {{contrato}}
Data do pagamento: {{dataPagamento}}

{{cidade}}, {{data}}.


______________________________________
{{emitente}}`;

export default function ReceiptTemplatesPage() {
  const [templates, setTemplates] = useState<ReceiptTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<ReceiptTemplate | null>(null);
  const [form, setForm] = useState({ name: '', content: STARTER, isDefault: false, isActive: true });
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/receipts/templates');
      setTemplates(data || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  function openNew() {
    setEditing(null);
    setForm({ name: '', content: STARTER, isDefault: templates.length === 0, isActive: true });
  }
  function openEdit(t: ReceiptTemplate) {
    setEditing(t);
    setForm({ name: t.name, content: t.content, isDefault: t.isDefault, isActive: t.isActive });
  }

  async function save() {
    if (!form.name.trim() || !form.content.trim()) {
      alert('Informe nome e conteúdo.');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/receipts/templates/${editing.id}`, form);
      } else {
        await api.post('/receipts/templates', form);
      }
      setEditing(null);
      setForm({ name: '', content: STARTER, isDefault: false, isActive: true });
      setShowModal(false);
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Falha ao salvar template.');
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    if (!confirm('Remover este template?')) return;
    await api.delete(`/receipts/templates/${id}`);
    load();
  }

  const [showModal, setShowModal] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold dark:text-dark-text-primary flex items-center gap-2">
            <FileText className="w-6 h-6" /> Templates de Recibo
          </h1>
          <p className="text-sm text-gray-500 dark:text-dark-text-secondary">
            Modelos usados ao emitir recibos nas cobranças.
          </p>
        </div>
        <button
          onClick={() => {
            openNew();
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg"
        >
          <Plus className="w-4 h-4" /> Novo template
        </button>
      </div>

      <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg divide-y divide-gray-100 dark:divide-dark-border">
        {loading && <p className="p-4 text-sm text-gray-400">Carregando...</p>}
        {!loading && templates.length === 0 && (
          <p className="p-4 text-sm text-gray-400">Nenhum template. Crie o primeiro (vira o padrão).</p>
        )}
        {templates.map((t) => (
          <div key={t.id} className="flex items-center justify-between p-4">
            <div>
              <div className="font-medium dark:text-dark-text-primary flex items-center gap-2">
                {t.name}
                {t.isDefault && (
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3" /> Padrão
                  </span>
                )}
                {!t.isActive && <span className="text-xs text-gray-400">(inativo)</span>}
              </div>
              <p className="text-xs text-gray-400 line-clamp-1 max-w-xl">{t.content.slice(0, 90)}…</p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => { openEdit(t); setShowModal(true); }} className="p-2 text-gray-500 hover:text-primary-600">
                <Edit2 className="w-4 h-4" />
              </button>
              <button onClick={() => remove(t.id)} className="p-2 text-gray-500 hover:text-red-600">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <Modal isOpen onClose={() => setShowModal(false)} title={editing ? 'Editar template' : 'Novo template'} size="2xl">
          <div className="space-y-4">
            <Input label="Nome" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-primary mb-1">Conteúdo</label>
              <textarea
                className="input min-h-[260px] font-mono text-sm w-full"
                value={form.content}
                onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
              />
              <p className="text-xs text-gray-400 mt-1">Variáveis: {VARS.join(' ')}</p>
            </div>
            <div className="flex gap-4 text-sm dark:text-dark-text-primary">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.isDefault} onChange={(e) => setForm((p) => ({ ...p, isDefault: e.target.checked }))} />
                Definir como padrão
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />
                Ativo
              </label>
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={save} disabled={saving} className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
